import { useState } from 'react'
import { Form, useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'

import { CompanyNameInput } from '@dhl/inputs'
import { CheckboxInput, SelectInput, TextFieldInput } from '@dhl/formik'
import { AddressInput } from '@dhl/input-address'
import { useGetBusinessUnit } from '@dhl/mdp-api'
import { Box, Button, Checkbox, Grid, H6, MenuItem } from '@dhl-ui/components'

import { useDataAsyncCall } from '~/api/admin-api'
import { COUNTRIES } from '~/common/config/country'
import { CountrySelect } from '~/common/components/addresses'
import type { Terminal } from '~/modules/terminals/model'

type Props = {
  country: string
  type?: string
  onCancel: () => void
  onChangeCountry: (value: string) => void
}

type ResponsePossibleBusinessUnit = {
  businessUnitCodes: string[]
}

type ResponsePossibleCountries = {
  countries: string[]
}

export const TerminalForm = ({ type, onChangeCountry, onCancel, country }: Props) => {
  const { t } = useTranslation()
  const { setFieldValue, values } = useFormikContext<Terminal>()
  const [sharedShipments, setSharedShipments] = useState(values.useSharedShipments)

  const { data: allBusinessUnits } = useGetBusinessUnit()

  const [possibleBusinessUnitsResponse] = useDataAsyncCall<ResponsePossibleBusinessUnit>(
    `admin-user/possible-business-unit-codes`
  )
  const possibleBusinessUnitsCodes = possibleBusinessUnitsResponse?.businessUnitCodes

  const [possibleCountriesResponse] = useDataAsyncCall<ResponsePossibleCountries>(`admin-user/possible-countries`)
  const possibleCountriesCodes = possibleCountriesResponse?.countries

  if (!allBusinessUnits && !possibleBusinessUnitsCodes && !possibleCountriesCodes) return null

  const possibleBusinessUnits = allBusinessUnits?.filter((item: { code: string }) => {
    return possibleBusinessUnitsCodes?.some((value: string) => {
      return item.code === value
    })
  })

  possibleBusinessUnits?.sort((preIndex, postIndex) => {
    if (preIndex.name.toLocaleLowerCase() < postIndex.name.toLocaleLowerCase()) {
      return -1
    }
    if (preIndex.name.toLocaleLowerCase() > postIndex.name.toLocaleLowerCase()) {
      return 1
    }
    return 0
  })

  const possibleCountries = COUNTRIES.filter(({ value: itemValue }) => {
    return possibleCountriesCodes?.some((value: string) => {
      return itemValue === value
    })
  })

  const onChangeSharedShipments = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setSharedShipments(event.target.checked)
    await setFieldValue('useSharedShipments', event.target.checked)
    if (!event.target.checked) {
      await setFieldValue('useSharedDrafts', false)
    }
  }

  const onChangeSharedDrafts = (event: React.ChangeEvent<HTMLInputElement>) =>
    setFieldValue('useSharedDrafts', event.target.checked)

  return (
    <Form data-testid="terminal-form">
      <Box pt={1} pb={1}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <H6>{t('admin.terminalsPage.terminalForm.accountInformation')}</H6>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <CompanyNameInput name="name" fullWidth />
          </Grid>
          <Grid size={{ xs: 6 }}>
            {possibleBusinessUnits && (
              <SelectInput fullWidth name="businessUnitCode" data-testid="businessUnitCode" label="business unit">
                {possibleBusinessUnits.map((businessUnit: { code: string; name: string }) => (
                  <MenuItem key={businessUnit.code} value={businessUnit.code}>
                    {businessUnit.name}
                  </MenuItem>
                ))}
              </SelectInput>
            )}
          </Grid>
          <Grid>
            <H6>{t('admin.terminalsPage.terminalForm.addressInformation')}</H6>
          </Grid>
          <Grid>
            <AddressInput carrier="parcel" country={country} name="data.address" />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <CountrySelect
              fieldName="data.address.countryCode"
              onChangeCountryCode={onChangeCountry}
              countries={possibleCountries}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <H6>{t('admin.terminalsPage.terminalForm.contact')}</H6>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextFieldInput
              fullWidth
              label={t('admin.terminalsPage.terminalForm.firstName')}
              name="data.contactPerson.firstName"
              type="text"
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextFieldInput
              fullWidth
              label={t('admin.terminalsPage.terminalForm.lastName')}
              name="data.contactPerson.lastName"
              type="text"
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextFieldInput
              fullWidth
              label={t('admin.terminalsPage.terminalForm.emailAddress')}
              name="data.contactPerson.email"
              type="text"
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextFieldInput
              fullWidth
              label={t('admin.terminalsPage.terminalForm.phone')}
              name="data.contactPerson.phone"
              type="text"
            />
          </Grid>
          <Grid container direction="column" alignItems="flex-start" pt={1} size={{ xs: 12 }} spacing={0}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Checkbox
                name="useSharedShipments"
                checked={values.useSharedShipments}
                onChange={onChangeSharedShipments}
                label={t('admin.terminalsPage.terminalForm.sharedShipments').toString()}
              />
              <Box>
                <CheckboxInput
                  name="useCompanyAddressBook"
                  label={t('admin.terminalsPage.terminalForm.companyAddressBook').toString()}
                />
              </Box>

              <Checkbox
                name="useSharedDrafts"
                checked={values.useSharedDrafts}
                onChange={onChangeSharedDrafts}
                disabled={!sharedShipments}
                label={t('admin.terminalsPage.terminalForm.sharedDrafts').toString()}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box marginTop={2} marginBottom={2} display="flex" justifyContent="flex-end">
        <Box marginRight={1}>
          <Button color="primary" variant="text" size="medium" onClick={onCancel}>
            {t('admin.terminalsPage.terminalForm.cancel')}
          </Button>
        </Box>
        <Button color="primary" type="submit" variant="contained" size="medium">
          {type
            ? t('admin.terminalsPage.terminalForm.addTerminal')
            : t('admin.terminalsPage.terminalForm.updateTerminal')}
        </Button>
      </Box>
    </Form>
  )
}
